import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import styled from 'styled-components';
import Loading from '../../../../shared/loading';
import CompaniesContext from './context';
import { HeaderMixin } from '../../shared/styles';
import TextArea from '../../../shared/text-area';

const SModal = styled(Modal)`
  & > article.horizontal {
    min-width: 700px;
    min-height: 670px;
  }
`;

const Title = styled.h5`
  ${HeaderMixin};
  font-size: 18px;
  text-align: center;
  margin-bottom: 16px;
`;

const Body = styled(Modal.Body)`
  display: flex;
  flex-direction: column;
  padding: 20px 24px 0;
  height: 100%;

  label {
    text-transform: none;
    font-size: 16px;

    textarea {
      border-radius: 4px;
      margin-top: 4px;
      resize: none;
      width: 100%;
    }
  }

  .sip-controls {
    margin: 0;
    line-height: 1.4;
  }
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 22px;
  padding: 0 24px;
  margin-bottom: 0;
`;

const Success = styled.div`
  margin-bottom: 16px;
  font-size: 16px;
  color: #4CAF50;
`;

const BtnGroup = styled.div`
  margin: 24px;
  display: flex;
  justify-content: flex-end;
`;

const defaultCTA = {
  title: '',
  message: '',
  text: '',
  url: '',
};

export default function CTAModal({
  type,
  title,
  description,
  dispatchEvent,
  createFunction,
  updateFunction,
}) {
  const {
    state: { showCompanyCTAModal, showContributorCTAModal, currentEditingCTAOwner: owner },
    dispatch,
  } = React.useContext(CompaniesContext);

  const [cta, setCta] = React.useState(owner.cta ?? defaultCTA);

  React.useEffect(() => {
    setCta(owner.cta ?? defaultCTA);
  }, [owner]);

  const updateCTA = (field, value) => setCta({ ...cta, [field]: value });

  const [errors, setErrors] = React.useState({});

  const errorOn = (field, fieldLabel) => {
    if (errors[field]) {
      return `${fieldLabel} ${errors[field][0]}`;
    }

    return '';
  };

  const [message, setMessage] = React.useState('');

  const dismiss = e => {
    e && e.preventDefault();

    setMessage('');
    setErrors({});
    dispatch({ type: 'dismissModal' });
  };

  const loadingRef = React.useRef();

  const saveCTA = e => {
    e && e.preventDefault();

    const saveFunc = cta.id ? updateFunction : createFunction;

    saveFunc(owner.id, cta)
      .then(({ data }) => {
        updateCTA('id', data.id);
        owner.cta = cta;
        setErrors({});
        setMessage('CTA saved successfully');

        if (dispatchEvent) {
          dispatch({ type: dispatchEvent, payload: owner });
        }

        setTimeout(dismiss, 1000);
      })
      .catch(err => {
        setErrors(err.response.data);
        setMessage('');
      });
  };

  return (
    <SModal
      centered="true"
      dialogAs="article"
      dialogClassName="horizontal"
      show={(type === 'company' && showCompanyCTAModal) || (type === 'contributor' && showContributorCTAModal)}
      onHide={dismiss}
    >
      <Modal.Header>
        <Title>{title}</Title>
        <Description>{description}</Description>
      </Modal.Header>
      <Body>
        <Success>
          {message}
        </Success>
        <TextArea
          value={cta.title}
          label="CTA Title"
          placeholder="Add concise, engaging title"
          height={44}
          limit={100}
          isLoading={false}
          error={errorOn('title', 'Title')}
          onChange={v => updateCTA('title', v)}
        />
        <TextArea
          value={cta.message}
          label="Message"
          placeholder="Add concise message to encourage readers to engage"
          height={90}
          limit={300}
          isLoading={false}
          error={errorOn('message', 'Message')}
          onChange={v => updateCTA('message', v)}
        />
        <TextArea
          value={cta.text}
          label="Call to action text"
          placeholder="Add succinct Call To Action text"
          height={44}
          limit={50}
          isLoading={false}
          error={errorOn('text', 'Call to action text')}
          onChange={v => updateCTA('text', v)}
        />
        <TextArea
          value={cta.url}
          label="Call to action link"
          placeholder="Add website URL"
          height={44}
          limit={null}
          isLoading={false}
          error={errorOn('url', 'Call to action link')}
          onChange={v => updateCTA('url', v)}
        />
      </Body>
      <Modal.Footer as="footer">
        <BtnGroup>
          <Loading variant="sip" ref={loadingRef} />
          <button type="button" className="btn btn-secondary" onClick={dismiss}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={saveCTA}>
            Save
          </button>
        </BtnGroup>
      </Modal.Footer>
    </SModal>
  );
}

CTAModal.propTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  dispatchEvent: PropTypes.string.isRequired,
  createFunction: PropTypes.func.isRequired,
  updateFunction: PropTypes.func.isRequired,
};
